import { useState, useEffect, useCallback } from 'react';
import {
  History,
  Clock,
  User,
  ChevronRight,
  RotateCcw,
  Eye,
  X,
  GitCommit,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime, cn } from '@/lib/utils';
import { artifactService, type ArtifactVersion } from '@/services/artifactService';

export interface Version {
  id: string;
  version: number;
  title: string;
  summary: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  createdAt: string;
  changes: {
    additions: number;
    deletions: number;
  };
}

interface VersionHistoryProps {
  versions: Version[];
  currentVersion: number;
  onRestore: (version: Version) => void;
  isLoading?: boolean;
}

export function VersionHistory({
  versions,
  currentVersion,
  onRestore,
  isLoading,
}: VersionHistoryProps) {
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreview = (version: Version) => {
    setSelectedVersion(version);
    setPreviewOpen(true);
  };

  const handleRestore = (version: Version) => {
    onRestore(version);
    setPreviewOpen(false);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <History className="mr-1 h-4 w-4" />
          History
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Version History
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-20 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          ) : versions.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <GitCommit className="mx-auto h-12 w-12 opacity-50" />
              <p className="mt-4">No version history yet</p>
              <p className="mt-1 text-sm">
                Version history will appear here when changes are made.
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-150px)]">
              <div className="space-y-2 pr-4">
                {versions.map((version) => (
                  <VersionItem
                    key={version.id}
                    version={version}
                    isCurrent={version.version === currentVersion}
                    onPreview={() => handlePreview(version)}
                    onRestore={() => handleRestore(version)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>

      {/* Version Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Version {selectedVersion?.version} Preview</span>
              <div className="flex items-center gap-2">
                {selectedVersion &&
                  selectedVersion.version !== currentVersion && (
                    <Button
                      size="sm"
                      onClick={() =>
                        selectedVersion && handleRestore(selectedVersion)
                      }
                    >
                      <RotateCcw className="mr-1 h-4 w-4" />
                      Restore this version
                    </Button>
                  )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPreviewOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedVersion && (
            <div className="mt-4">
              <div className="mb-4 flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {selectedVersion.author.name}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDateTime(selectedVersion.createdAt)}
                </span>
                <Badge variant="outline" className="text-xs">
                  +{selectedVersion.changes.additions} / -
                  {selectedVersion.changes.deletions}
                </Badge>
              </div>

              <ScrollArea className="h-[50vh] rounded-lg border bg-muted/30 p-4">
                <pre className="whitespace-pre-wrap font-mono text-sm">
                  {selectedVersion.content}
                </pre>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}

function VersionItem({
  version,
  isCurrent,
  onPreview,
  onRestore,
}: {
  version: Version;
  isCurrent: boolean;
  onPreview: () => void;
  onRestore: () => void;
}) {
  return (
    <div
      className={cn(
        'group relative rounded-lg border p-3 transition-colors hover:bg-muted/50',
        isCurrent && 'border-primary bg-primary/5'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge
              variant={isCurrent ? 'default' : 'outline'}
              className="text-xs"
            >
              v{version.version}
            </Badge>
            {isCurrent && (
              <Badge variant="secondary" className="text-xs">
                Current
              </Badge>
            )}
          </div>

          <h4 className="mt-1.5 font-medium truncate">{version.title}</h4>

          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {version.summary}
          </p>

          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {version.author.name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDateTime(version.createdAt)}
            </span>
            <span className="text-green-600">+{version.changes.additions}</span>
            <span className="text-red-600">-{version.changes.deletions}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" onClick={onPreview}>
            <Eye className="h-4 w-4" />
          </Button>
          {!isCurrent && (
            <Button variant="ghost" size="icon" onClick={onRestore}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}

// ============================================
// API-connected version history hook
// ============================================

interface UseArtifactVersionHistoryResult {
  versions: Version[];
  currentVersion: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  restoreVersion: (versionNumber: number) => Promise<void>;
}

export function useArtifactVersionHistory(
  projectId: string | undefined,
  artifactId: string | undefined
): UseArtifactVersionHistoryResult {
  const [versions, setVersions] = useState<Version[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVersions = useCallback(async () => {
    if (!projectId || !artifactId) {
      setVersions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await artifactService.getVersions(projectId, artifactId);

      // Transform ArtifactVersion to Version format
      const transformedVersions: Version[] = response.versions.map((v: ArtifactVersion) => ({
        id: v.id,
        version: v.versionNumber,
        title: v.changeSummary || `버전 ${v.versionNumber}`,
        summary: v.changeSummary || '',
        content: '', // Content is loaded on demand when previewing
        author: {
          id: '',
          name: v.createdByName || '알 수 없음',
        },
        createdAt: v.createdAt,
        changes: {
          additions: v.additions,
          deletions: v.deletions,
        },
      }));

      setVersions(transformedVersions);
      setCurrentVersion(response.currentVersion);
    } catch (err) {
      console.error('Failed to load version history:', err);
      setError('버전 히스토리를 불러오는데 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, artifactId]);

  const restoreVersion = useCallback(async (versionNumber: number) => {
    if (!projectId || !artifactId) return;

    try {
      await artifactService.restoreVersion(projectId, artifactId, versionNumber);
      // Reload versions after restore
      await loadVersions();
    } catch (err) {
      console.error('Failed to restore version:', err);
      throw err;
    }
  }, [projectId, artifactId, loadVersions]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  return {
    versions,
    currentVersion,
    isLoading,
    error,
    refresh: loadVersions,
    restoreVersion,
  };
}

// Wrapper component that uses the API hook
interface ArtifactVersionHistoryProps {
  projectId: string;
  artifactId: string;
  onRestore?: () => void;
}

export function ArtifactVersionHistory({
  projectId,
  artifactId,
  onRestore,
}: ArtifactVersionHistoryProps) {
  const { versions, currentVersion, isLoading, restoreVersion } = useArtifactVersionHistory(
    projectId,
    artifactId
  );

  const handleRestore = async (version: Version) => {
    try {
      await restoreVersion(version.version);
      onRestore?.();
    } catch (err) {
      console.error('Failed to restore version:', err);
    }
  };

  return (
    <VersionHistory
      versions={versions}
      currentVersion={currentVersion}
      onRestore={handleRestore}
      isLoading={isLoading}
    />
  );
}

// Mock versions for demo (kept for backwards compatibility)
export function useMockVersionHistory(): Version[] {
  return [
    {
      id: '1',
      version: 3,
      title: 'Added API endpoints section',
      summary:
        'Added comprehensive API documentation with endpoints, request/response examples, and authentication details.',
      content: `# Project Specification v3

## API Endpoints

### Authentication
POST /api/v1/auth/login
POST /api/v1/auth/register

### Projects
GET /api/v1/projects
POST /api/v1/projects
GET /api/v1/projects/:id
...`,
      author: { id: '1', name: 'John Doe' },
      createdAt: new Date().toISOString(),
      changes: { additions: 45, deletions: 5 },
    },
    {
      id: '2',
      version: 2,
      title: 'Updated requirements section',
      summary:
        'Refined functional requirements based on client feedback and added new features.',
      content: `# Project Specification v2

## Requirements

### Functional Requirements
- FR-1: User authentication
- FR-2: Project management
...`,
      author: { id: '2', name: 'Jane Smith' },
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      changes: { additions: 20, deletions: 8 },
    },
    {
      id: '3',
      version: 1,
      title: 'Initial specification',
      summary: 'Created initial project specification document with overview and basic requirements.',
      content: `# Project Specification v1

## Overview
Initial project specification...`,
      author: { id: '1', name: 'John Doe' },
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      changes: { additions: 100, deletions: 0 },
    },
  ];
}
